import { cva } from "class-variance-authority";

// The baseline underline mirrors the scroller's padding so it lines up with
// the first/last items' edges instead of leaving a visible gray sliver in
// the 3px ring-breathing-room strip on either side.
export const tabsWrapperVariants = cva(
  "after:bg-shape-neutral-outlined-border-default relative flex w-full min-w-0 items-center after:pointer-events-none after:absolute after:right-[3px] after:bottom-[5px] after:left-[3px] after:z-0 after:h-px after:content-['']"
);

// `overflow-x-auto` promotes `overflow-y` to clip too, so the item's
// focus-visible ring (a `::before` extending -3px top/left/right and -5px
// bottom) gets cut off. The padding here matches those ring offsets so the
// ring has room to render inside the scroller's clipping box.
export const tabsScrollerVariants = cva(
  "flex w-full min-w-0 items-center gap-4 overflow-x-auto px-[3px] pt-[3px] pb-[5px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
);

export const tabsItemVariants = cva(
  [
    "text-shape-neutral-outlined-foreground-muted relative z-0 inline-flex h-11 shrink-0 cursor-pointer items-center justify-center border-b-2 border-transparent p-2 text-center text-sm leading-5 font-normal whitespace-nowrap transition-[color] outline-none",
    "hover:text-shape-neutral-outlined-foreground-default hover:border-shape-neutral-outlined-border-default",
    "focus-visible:before:border-ring-default focus-visible:before:pointer-events-none focus-visible:before:absolute focus-visible:before:-top-[3px] focus-visible:before:-right-[3px] focus-visible:before:-bottom-[5px] focus-visible:before:-left-[3px] focus-visible:before:z-10 focus-visible:before:rounded-lg focus-visible:before:border-3 focus-visible:before:content-['']",
  ],
  {
    variants: {
      // Each Radix primitive exposes "active" via a different data attribute:
      // - Radix Tabs renders a <button> with data-state="active" and uses
      //   the native `disabled` pseudo-class for the disabled state.
      // - Radix NavigationMenu renders an <a> with data-active and relies on
      //   `aria-disabled` for the disabled state.
      activeSelector: {
        state:
          "data-[state=active]:text-shape-neutral-outlined-foreground-default data-[state=active]:border-shape-neutral-outlined-border-default-selected disabled:text-foreground-neutral-default disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 data-[state=active]:z-10",
        active:
          "data-[active]:text-shape-neutral-outlined-foreground-default data-[active]:border-shape-neutral-outlined-border-default-selected gap-2 aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed aria-disabled:opacity-50 data-[active]:z-10",
      },
    },
    defaultVariants: { activeSelector: "state" },
  }
);

export const tabsScrollButtonVariants = cva(
  [
    // `bottom-[6px]` stops the gradient just above the wrapper's baseline
    // underline (at `bottom-[5px]`, h-px) so the line stays continuous behind
    // the chevron instead of being clipped by its background.
    "text-shape-neutral-ghost-foreground-muted absolute top-0 bottom-[6px] z-20 flex w-[34px] items-center justify-center transition-[color,opacity] outline-none",
    "hover:text-shape-neutral-ghost-foreground-default",
    "disabled:pointer-events-none disabled:opacity-0",
    // Extend the pointer target to 44px wide while keeping the chevron visually
    // 28px — meets WCAG 2.5.5 (AAA) touch-target guidance on touch devices
    // without altering the layout chrome.
    "before:absolute before:inset-y-0 before:-right-2 before:-left-2 before:content-['']",
    // Match the design system's focus-visible ring used by Button/Tabs rather
    // than the browser-default blue outline.
    "focus-visible:ring-ring-default focus-visible:ring-2 focus-visible:ring-offset-1",
  ],
  {
    variants: {
      // The button is 34px wide: 28px of solid ghost background where the
      // chevron sits with equal padding on both sides, plus a 6px gradient
      // strip on the inner edge that fades into the tabs. Padding
      // (`pr-1.5`/`pl-1.5`) anchors the icon in the outer 28px solid section
      // so it's never dragged into the gradient zone.
      direction: {
        left: "left-0 bg-[linear-gradient(to_right,var(--color-page-default),var(--color-page-default)_calc(100%-6px),transparent)] pr-1.5",
        right:
          "right-0 bg-[linear-gradient(to_left,var(--color-page-default),var(--color-page-default)_calc(100%-6px),transparent)] pl-1.5",
      },
    },
  }
);
